import React, { lazy } from "react";
import { Switch, BrowserRouter, Redirect } from "react-router-dom";

import ScrollToTop from "./ScrollToTop";
import ReduxToastr from "react-redux-toastr";
import NotFound from "../components/Pages/Public/NotFound";

// import for route access permissions
import PublicRoute from "./RouteWrappers/PublicRoute";
import DefaultRoute from "./RouteWrappers/DefaultRoute";
import PrivateRoute from "./RouteWrappers/PrivateRoute";
import OnboardingRoute from "./RouteWrappers/OnboardingRoute";
import CashierPublicRoute from "./RouteWrappers/CashierPublicRoute";
import CashierPrivateRoute from "./RouteWrappers/CashierPrivateRoute";

// auth imports
const Login = lazy(() => import("../components/Pages/Public/Login"));
const Signup = lazy(() => import("../components/Pages/Public/Signup"));
const VerifyEmail = lazy(() =>
  import("../components/Pages/Public/VerifyEmail")
);
const LoginWithOtp = lazy(() =>
  import("../components/Pages/Public/Login/WithOtp")
);
const ResetPassword = lazy(() =>
  import("../components/Pages/Public/ResetPassword")
);
const ForgotPassword = lazy(() =>
  import("../components/Pages/Public/ForgotPassword")
);

const Shortlink = lazy(() => import("../components/Pages/Public/Shortlink"));
const ApprovalLink = lazy(() => import("../components/Pages/Public/Approval"));

const Plans = lazy(() => import("../components/Pages/Private/Plans"));
const Account = lazy(() => import("../components/Pages/Private/Account"));
const Dashboard = lazy(() => import("../components/Pages/Private/Dashboard"));
const CustomerInsights = lazy(() =>
  import("../components/Pages/Private/CustomerInsights")
);

const CaptureCustomer = lazy(() =>
  import("../components/Pages/Private/CaptureCustomer")
);
const ImportSuccess = lazy(() =>
  import(
    "../components/Pages/Private/CaptureCustomer/ImportCustomerPage/Success"
  )
);
const ImportLogs = lazy(() =>
  import("../components/Pages/Private/CaptureCustomer/Logs")
);
const IntegrationDetail = lazy(() =>
  import("../components/Pages/Private/CaptureCustomer/IntegrationDetail")
);

// store creation imports
const Stores = lazy(() => import("../components/Pages/Private/Stores"));
const OnboardingStep1 = lazy(() =>
  import("../components/Pages/Private/Onboarding/Step1")
);
const OnboardingStep2 = lazy(() =>
  import("../components/Pages/Private/Onboarding/Step2")
);
const OnboardingStep3 = lazy(() =>
  import("../components/Pages/Private/Onboarding/Step3")
);
const OnboardingStep4 = lazy(() =>
  import("../components/Pages/Private/Onboarding/Step4")
);
const OnboardingStep5 = lazy(() =>
  import("../components/Pages/Private/Onboarding/Step5")
);
const AddStoreStep1 = lazy(() =>
  import("../components/Pages/Private/Stores/new/step-1")
);
const AddStoreStep2 = lazy(() =>
  import("../components/Pages/Private/Stores/new/step-2")
);
const AddStoreStep3 = lazy(() =>
  import("../components/Pages/Private/Stores/new/step-3")
);
const AddStoreStep4 = lazy(() =>
  import("../components/Pages/Private/Stores/new/step-4")
);
// const AddStoreStep5 = lazy(() =>
//   import("../components/Pages/Private/Stores/new/step-5")
// );
const AddStoreStep6 = lazy(() =>
  import("../components/Pages/Private/Stores/new/step-6")
);
const AddStoreStep7 = lazy(() =>
  import("../components/Pages/Private/Stores/new/step-7")
);
const AddStoreSuccess = lazy(() =>
  import("../components/Pages/Private/Stores/new/success")
);

// campaign imports
const Campaigns = lazy(() => import("../components/Pages/Private/Campaigns"));
const CampsByCategory = lazy(() =>
  import("../components/Pages/Private/Campaigns/CampsByCategory")
);
const AllCampaigns = lazy(() =>
  import("../components/Pages/Private/Campaigns/Overview/All")
);
const CampDetails = lazy(() =>
  import("../components/Pages/Private/Campaigns/Overview/CampDetails")
);
// edit campaign imports
const CampaignRewardDetails = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/RewardDetails")
);
const CampaignTerms = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Terms")
);
const CampaignTheme = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Theme")
);
const CampaignWhatsappOptions = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/WhatsappMsgOptions")
);
const CampaignWhatsapp = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Whatsapp")
);
const CampaignSms = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/SMS")
);
const CampaignEmail = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Email")
);
const CampaignAudience = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Audience")
);
const CampaignRedeemableStores = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/RedeemableStores")
);
const CampaignChannels = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Channels")
);
const NewCampaignTime = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Time")
);
const CampaignReview = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Review")
);
const CampaignSuccess = lazy(() =>
  import("../components/Pages/Private/Campaigns/Edit/Success")
);

// loyalty imports
// * loyalty root route
const CheckLoyalty = lazy(() => import("../components/Pages/Private/Loyalty"));

// * Loyalty setup routes
const LoyaltyTypes = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/LoyaltyTypes")
);
const LoyaltyEditCreative = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/Creative")
);
const LoyaltyEditCashbackTypes = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/CashbackTypes")
);
const LoyaltyEditBonus = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/Bonus")
);
const LoyaltyEditReminders = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/Reminders")
);
const LoyaltyEditChannels = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/Channels")
);
const LoyaltyEditFraudAlert = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/FraudAlert")
);
const LoyaltyEditReview = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/Review")
);

// * Loyalty insights routes
const LoyaltyInsights = lazy(() =>
  import("../components/Pages/Private/Loyalty/Insights")
);
const LoyaltyActivity = lazy(() =>
  import("../components/Pages/Private/Loyalty/Insights/Activity")
);
const RewardScoreBoard = lazy(() =>
  import("../components/Pages/Private/Loyalty/Insights/RewardScoreboard")
);
const TopRedeemingCustomers = lazy(() =>
  import("../components/Pages/Private/Loyalty/Insights/TopRedeemingCustomers")
);

// * Feedback Routes

const CheckFbStatus = lazy(() =>
  import("../components/Pages/Private/FeedBack")
);

// * Feedback insights imports

const FbInsights = lazy(() =>
  import("../components/Pages/Private/FeedBack/Insights")
);
const FbList = lazy(() =>
  import("../components/Pages/Private/FeedBack/Insights/AllFeedbacks")
);
const AllCompliments = lazy(() =>
  import("../components/Pages/Private/FeedBack/Insights/AllCompliments")
);

// * Edit feedback component imports

const FbQuestion = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/Question")
);
const FbCategory = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/Category")
);
const FbHearAboutUs = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/HearAboutUs")
);
const FbReward = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/Reward")
);
const FbTime = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/Time")
);
const FbTheme = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/Theme")
);
const FbChannels = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/Channels")
);
const FbNegAlert = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/Alert")
);
const FbOnlineReviews = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/OnlineReviews")
);
const ReviewFbDetails = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/ReviewFbDetails")
);
const FbSuccess = lazy(() =>
  import("../components/Pages/Private/FeedBack/Edit/pages/Success")
);

// * Automated campaign imports

const AutoEngage = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign")
);
const AEChannel = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/Edit/pages/Channel")
);
const AeCampaignTime = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/Edit/pages/Time")
);
const AeCampaignReview = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/Edit/pages/Review")
);
const AeCampaignSuccess = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/Edit/pages/Success")
);
const AutomatedCampDetail = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/AutomatedCampDetail")
);
const ObjectiveTemplates = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/ObjectiveTemplates")
);
const EditAutomatedCampaign = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/Edit/pages/Creative")
);
const WhatsappAutoCampaign = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/Edit/pages/Whatsapp")
);
const EditAutomatedCampaignSms = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/Edit/pages/SMS")
);
const EditAutomatedCampaignEmail = lazy(() =>
  import("../components/Pages/Private/Auto-Campaign/Edit/pages/Email")
);
// channels imports
const Channels = lazy(() => import("../components/Pages/Private/Channels"));
const ChannelDetail = lazy(() =>
  import("../components/Pages/Private/Channels/ChannelDetail")
);
const RefillHistory = lazy(() =>
  import("../components/Pages/Private/Channels/RefillHistory")
);
const ChannelSettings = lazy(() =>
  import("../components/Pages/Private/Channels/ChannelSettings")
);
const WhatsappSetup = lazy(() =>
  import("../components/Pages/Private/Channels/ChannelSettings/Whatsapp/Setup")
);

// cashier imports
const Cashier = lazy(() => import("../components/Pages/Private/Cashier"));
const RedeemReward = lazy(() =>
  import("../components/Pages/Private/Cashier/RedeemReward")
);
const CashierLogin = lazy(() =>
  import("../components/Pages/Private/Cashier/CashierLogin")
);
const CashierHistory = lazy(() =>
  import("../components/Pages/Private/Cashier/CashierHistory")
);
const CashierStoreIdNotFound = lazy(() =>
  import("../components/Pages/Private/Cashier/CashierStoreIdNotFound")
);

// QR Code
const QrIndex = lazy(() => import("../components/Pages/Private/QrCode"));
const QrHowItWorks = lazy(() =>
  import("../components/Pages/Private/QrCode/HowItWorks")
);
const QrCodeName = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/Name")
);
const QrCodeCollectData = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/CollectData")
);
const QrCaptureViaWhatsApp = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/CaptureViaWa")
);
const QrCodeReward = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/Reward")
);
const QrCodeTerms = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/Terms")
);
const QrCodeStores = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/SelectStores")
);
const QrCodeReminder = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/Reminder")
);
const QrCodeTheme = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/Theme")
);
const QrCodeCreative = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/Creative")
);
const QrCodeReview = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/Review")
);
const QrCodeSuccess = lazy(() =>
  import("../components/Pages/Private/QrCode/Edit/Pages/Success")
);
const QrDetail = lazy(() =>
  import("../components/Pages/Private/QrCode/Insights/QrDetail")
);
const DemoQrShortlink = lazy(() =>
  import("../components/Pages/Public/Shortlink/DemoQrShortlink/DemoQr")
);

// Import Transactions
const ImportTrxnsSuccess = lazy(() =>
  import(
    "../components/Pages/Private/CaptureCustomer/ImportTransactionsPage/Success"
  )
);
const ImportPosTransactionPage = lazy(() =>
  import(
    "../components/Pages/Private/CaptureCustomer/ImportTransactionsPage/ImportPosTransactions"
  )
);

// B2C Referral
const B2cIndex = lazy(() => import("../components/Pages/Private/B2cReferral"));
const B2cHowItWorks = lazy(() =>
  import("../components/Pages/Private/B2cReferral/HowItWorks")
);
const B2cInsights = lazy(() =>
  import("../components/Pages/Private/B2cReferral/Insights")
);
const B2cNewCustomer = lazy(() =>
  import("../components/Pages/Private/B2cReferral/Edit/Pages/New")
);
const B2cExistingCustomer = lazy(() =>
  import("../components/Pages/Private/B2cReferral/Edit/Pages/Existing")
);
const B2cChannels = lazy(() =>
  import("../components/Pages/Private/B2cReferral/Edit/Pages/Channels")
);
const B2cTest = lazy(() =>
  import("../components/Pages/Private/B2cReferral/Edit/Pages/Test")
);
const B2cSuccess = lazy(() =>
  import("../components/Pages/Private/B2cReferral/Edit/Pages/Success")
);
const NewCustomerReferralShortlink = lazy(() =>
  import("../components/Pages/Public/Shortlink/NewCustomerReferralShortlink")
);

const Checklist = lazy(() => import("../components/Pages/Private/Checklist"));
const NavigationMenu = lazy(() =>
  import("../components/Pages/Private/NavigationMenu")
);
const LoyaltyEditSuccess = lazy(() =>
  import("../components/Pages/Private/Loyalty/Edit/pages/Success")
);

const AutoRefillCredits = lazy(() =>
  import("../components/Pages/Private/AutoRefillCredits")
);

const WhatsappChat = lazy(() =>
  import("../components/Pages/Private/Chat/Whatsapp")
);
const InviteTeamMembers = lazy(() =>
  import("../components/Pages/Private/InviteTeamMembers")
);
const CreateTeamMemberRole = lazy(() =>
  import("../components/Pages/Private/InviteTeamMembers/Create/Pages/Role")
);
const CreateTeamMemberAccount = lazy(() =>
  import(
    "../components/Pages/Private/InviteTeamMembers/Create/Pages/AccountDetails"
  )
);
const SelectTeamMemberStores = lazy(() =>
  import("../components/Pages/Private/InviteTeamMembers/Create/Pages/Stores")
);
const AddTeamMemberPermissions = lazy(() =>
  import(
    "../components/Pages/Private/InviteTeamMembers/Create/Pages/Permissions"
  )
);
const ReviewTeamMember = lazy(() =>
  import("../components/Pages/Private/InviteTeamMembers/Create/Pages/Review")
);
const SuccessTeamMember = lazy(() =>
  import("../components/Pages/Private/InviteTeamMembers/Create/Pages/Success")
);

// Membership
const MembershipIndex = lazy(() =>
  import("../components/Pages/Private/Membership")
);

const Routes = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Switch>
        <PrivateRoute exact path="/" component={Dashboard} />
        {/* Public routes */}
        <DefaultRoute path="/verify-email" component={VerifyEmail} />
        <PublicRoute path="/signup" component={Signup} />
        <PublicRoute path="/login" component={Login} />
        <PublicRoute path="/login-otp" component={LoginWithOtp} />
        <PublicRoute path="/forgot-password" component={ForgotPassword} />
        <DefaultRoute path="/reset-password" component={ResetPassword} />

        <DefaultRoute exact path="/l" component={() => <Redirect to="/" />} />
        <DefaultRoute exact path="/l/:code" component={Shortlink} />
        <DefaultRoute path="/t/:templateCode" component={Shortlink} />
        <DefaultRoute path="/approval/:code" component={ApprovalLink} />
        <DefaultRoute path="/l/approval/:code" component={ApprovalLink} />
        <DefaultRoute exact path="/q/:code" component={DemoQrShortlink} />
        <DefaultRoute
          path="/l/referral/:code"
          component={NewCustomerReferralShortlink}
        />
        {/* Private routes */}
        <OnboardingRoute
          path="/onboarding/step-1"
          component={OnboardingStep1}
        />
        <OnboardingRoute
          path="/onboarding/step-2"
          component={OnboardingStep2}
        />
        <OnboardingRoute
          path="/onboarding/step-3"
          component={OnboardingStep3}
        />
        <OnboardingRoute
          path="/onboarding/step-4"
          component={OnboardingStep4}
        />
        <OnboardingRoute
          path="/onboarding/step-5"
          component={OnboardingStep5}
        />

        <PrivateRoute exact path="/loyalty" component={CheckLoyalty} />
        <PrivateRoute path="/loyalty/edit/types" component={LoyaltyTypes} />
        <PrivateRoute
          path="/loyalty/edit/cashback-types/:storeId"
          component={LoyaltyEditCashbackTypes}
        />
        <PrivateRoute
          path="/loyalty/edit/creative/:storeId"
          component={LoyaltyEditCreative}
        />
        <PrivateRoute
          path="/loyalty/edit/bonus/:storeId"
          component={LoyaltyEditBonus}
        />
        <PrivateRoute
          path="/loyalty/edit/reminder/:storeId"
          component={LoyaltyEditReminders}
        />
        <PrivateRoute
          path="/loyalty/edit/channels/:storeId"
          component={LoyaltyEditChannels}
        />
        <PrivateRoute
          path="/loyalty/edit/fraud-alert/:storeId"
          component={LoyaltyEditFraudAlert}
        />
        <PrivateRoute
          path="/loyalty/edit/review/:storeId"
          component={LoyaltyEditReview}
        />
        <PrivateRoute
          path="/loyalty/edit/success/:storeId"
          component={LoyaltyEditSuccess}
        />

        <PrivateRoute
          exact
          path="/loyalty/insights"
          component={LoyaltyInsights}
        />
        <PrivateRoute
          path="/loyalty/insights/activity"
          component={LoyaltyActivity}
        />
        <PrivateRoute
          path="/loyalty/insights/reward-scoreboard"
          component={RewardScoreBoard}
        />
        <PrivateRoute
          path="/loyalty/insights/top-redeeming-customers"
          component={TopRedeemingCustomers}
        />

        <PrivateRoute path="/account" component={Account} />

        <PrivateRoute exact path="/stores" component={Stores} />
        <PrivateRoute path="/stores/new/step-1" component={AddStoreStep1} />
        <PrivateRoute path="/stores/new/step-2" component={AddStoreStep2} />
        <PrivateRoute path="/stores/new/step-3" component={AddStoreStep3} />
        <PrivateRoute path="/stores/new/step-4" component={AddStoreStep4} />
        {/* <PrivateRoute path="/stores/new/step-5" component={AddStoreStep5} /> */}
        <PrivateRoute path="/stores/new/step-6" component={AddStoreStep6} />
        <PrivateRoute path="/stores/new/step-7" component={AddStoreStep7} />
        <PrivateRoute path="/stores/new/success" component={AddStoreSuccess} />

        <PrivateRoute path="/plans" component={Plans} />

        <PrivateRoute exact path="/campaigns" component={Campaigns} />
        <PrivateRoute
          path="/campaigns/objectives/:objective_key/:objective_value"
          component={CampsByCategory}
        />
        <PrivateRoute path="/campaigns/drafts" component={CampsByCategory} />
        <PrivateRoute path="/campaigns/existing" component={CampsByCategory} />
        <PrivateRoute
          path="/campaigns/:campId/channels/:entityId"
          component={CampaignChannels}
        />
        <PrivateRoute
          path="/campaigns/:campId/reward/:entityId"
          component={CampaignRewardDetails}
        />
        <PrivateRoute
          path="/campaigns/:campId/terms/:entityId"
          component={CampaignTerms}
        />
        <PrivateRoute
          path="/campaigns/:campId/theme/:entityId"
          component={CampaignTheme}
        />
        <PrivateRoute
          path="/campaigns/:campId/whatsapp/options/:entityId"
          component={CampaignWhatsappOptions}
        />
        <PrivateRoute
          path="/campaigns/:campId/whatsapp/:entityId"
          component={CampaignWhatsapp}
        />
        <PrivateRoute
          path="/campaigns/:campId/sms/:entityId"
          component={CampaignSms}
        />
        <PrivateRoute
          path="/campaigns/:campId/email/:entityId"
          component={CampaignEmail}
        />
        <PrivateRoute
          path="/campaigns/:campId/audience/:entityId"
          component={CampaignAudience}
        />
        <PrivateRoute
          path="/campaigns/:campId/redeemable-stores/:entityId"
          component={CampaignRedeemableStores}
        />
        <PrivateRoute
          path="/campaigns/:campId/time/:entityId"
          component={NewCampaignTime}
        />
        <PrivateRoute
          path="/campaigns/:campId/review/:entityId"
          component={CampaignReview}
        />
        <PrivateRoute
          path="/campaigns/:campId/success/:entityId"
          component={CampaignSuccess}
        />

        <PrivateRoute exact path="/campaigns/all" component={AllCampaigns} />
        <PrivateRoute
          path="/campaigns/detail/:campId"
          component={CampDetails}
        />
        <PrivateRoute
          exact
          path="/customer-insights"
          component={CustomerInsights}
        />
        <PrivateRoute
          path="/customer-insights/:sub_page"
          component={CustomerInsights}
        />
        <PrivateRoute exact path="/integration" component={CaptureCustomer} />
        <PrivateRoute
          exact
          path="/integration/pos"
          component={CaptureCustomer}
        />
        <PrivateRoute
          exact
          path="/integration/pos/:platform/:storeId"
          component={IntegrationDetail}
        />
        <PrivateRoute
          exact
          path="/integration/import"
          component={CaptureCustomer}
        />
        <PrivateRoute path="/integration/import/logs" component={ImportLogs} />
        <PrivateRoute
          path="/integration/import/success"
          component={ImportSuccess}
        />
        <PrivateRoute
          exact
          path="/integration/cashier"
          component={CaptureCustomer}
        />
        <PrivateRoute
          exact
          path="/integration/e-commerce"
          component={CaptureCustomer}
        />
        <PrivateRoute
          path="/integration/e-commerce/:platform/:storeId"
          component={IntegrationDetail}
        />
        <PrivateRoute
          exact
          path="/integration/import-loyalty-txns"
          component={CaptureCustomer}
        />
        <PrivateRoute
          exact
          path="/integration/import-loyalty-pos-txns"
          component={ImportPosTransactionPage}
        />
        <PrivateRoute
          path="/integration/import-loyalty-txns/logs"
          component={ImportLogs}
        />
        <PrivateRoute
          path="/integration/import-loyalty-txns/success"
          component={ImportTrxnsSuccess}
        />
        <PrivateRoute exact path="/channels" component={Channels} />
        <PrivateRoute
          path="/channels/detail/:channel_name"
          component={ChannelDetail}
        />
        <PrivateRoute
          exact
          path="/channels/settings"
          component={ChannelSettings}
        />
        <PrivateRoute
          exact
          path="/channels/settings/:channel"
          component={ChannelSettings}
        />
        <PrivateRoute
          path="/channels/settings/whatsapp/setup"
          component={WhatsappSetup}
        />
        <PrivateRoute
          path="/channels/refill-history"
          component={RefillHistory}
        />

        {/* Feedback */}

        <PrivateRoute exact path="/feedback" component={CheckFbStatus} />

        <PrivateRoute exact path="/feedback/insights" component={FbInsights} />
        <PrivateRoute exact path="/feedback/insights/all" component={FbList} />
        <PrivateRoute
          path="/feedback/insights/all/compliments"
          component={AllCompliments}
        />

        {/* Edit feedback */}
        <PrivateRoute
          path="/feedback/edit/:storeId/question"
          component={FbQuestion}
        />
        <PrivateRoute
          path="/feedback/edit/:storeId/category"
          component={FbCategory}
        />
        <PrivateRoute
          path="/feedback/edit/:storeId/hear-about-us"
          component={FbHearAboutUs}
        />
        <PrivateRoute
          path="/feedback/edit/:storeId/reward"
          component={FbReward}
        />
        <PrivateRoute path="/feedback/edit/:storeId/time" component={FbTime} />
        <PrivateRoute
          path="/feedback/edit/:storeId/theme"
          component={FbTheme}
        />
        <PrivateRoute
          path="/feedback/edit/:storeId/channels"
          component={FbChannels}
        />
        <PrivateRoute
          path="/feedback/edit/:storeId/alert"
          component={FbNegAlert}
        />
        <PrivateRoute
          path="/feedback/edit/:storeId/online-reviews"
          component={FbOnlineReviews}
        />
        <PrivateRoute
          path="/feedback/edit/:storeId/review"
          component={ReviewFbDetails}
        />
        <PrivateRoute
          path="/feedback/edit/:storeId/success"
          component={FbSuccess}
        />

        {/* Auto Campaigns */}

        <PrivateRoute exact path="/auto-campaigns" component={AutoEngage} />
        <PrivateRoute
          exact
          path="/auto-campaigns/:objective"
          component={ObjectiveTemplates}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/edit/:campId"
          component={EditAutomatedCampaign}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/whatsapp/:campId"
          component={WhatsappAutoCampaign}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/sms/:campId"
          component={EditAutomatedCampaignSms}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/email/:campId"
          component={EditAutomatedCampaignEmail}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/channel/:campId"
          component={AEChannel}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/time/:campId"
          component={AeCampaignTime}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/review/:campId"
          component={AeCampaignReview}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/success/:campId"
          component={AeCampaignSuccess}
        />
        <PrivateRoute
          path="/auto-campaigns/:objective/detail/:campId"
          component={AutomatedCampDetail}
        />

        {/* QR code */}

        <PrivateRoute exact path="/qr" component={QrIndex} />
        <PrivateRoute exact path="/qr/:activeTab" component={QrIndex} />
        <PrivateRoute path="/qr/how-it-works" component={QrHowItWorks} />
        <PrivateRoute
          path="/qr/edit/name/:qrId/:templateId"
          component={QrCodeName}
        />
        <PrivateRoute
          path="/qr/edit/collect-data/:qrId/:templateId"
          component={QrCodeCollectData}
        />
        <PrivateRoute
          path="/qr/edit/reward/:qrId/:templateId"
          component={QrCodeReward}
        />
        <PrivateRoute
          path="/qr/edit/terms/:qrId/:templateId"
          component={QrCodeTerms}
        />
        <PrivateRoute
          path="/qr/edit/whatsapp/:qrId/:templateId"
          component={QrCaptureViaWhatsApp}
        />
        <PrivateRoute
          path="/qr/edit/select-stores/:qrId/:templateId"
          component={QrCodeStores}
        />
        <PrivateRoute
          path="/qr/edit/reminder/:qrId/:templateId"
          component={QrCodeReminder}
        />
        <PrivateRoute
          path="/qr/edit/theme/:qrId/:templateId"
          component={QrCodeTheme}
        />
        <PrivateRoute
          path="/qr/edit/creative/:qrId/:templateId"
          component={QrCodeCreative}
        />
        <PrivateRoute
          path="/qr/edit/review/:qrId/:templateId"
          component={QrCodeReview}
        />
        <PrivateRoute
          path="/qr/edit/success/:qrId/:templateId"
          component={QrCodeSuccess}
        />

        <PrivateRoute path="/qr/insights/:qrId" component={QrDetail} />

        {/* B2C Referral */}
        <PrivateRoute exact path="/referrals/b2c" component={B2cIndex} />
        <PrivateRoute
          path="/referrals/b2c/how-it-works"
          component={B2cHowItWorks}
        />
        <PrivateRoute
          path="/referrals/b2c/edit/new"
          component={B2cNewCustomer}
        />
        <PrivateRoute
          path="/referrals/b2c/edit/existing"
          component={B2cExistingCustomer}
        />
        <PrivateRoute
          path="/referrals/b2c/edit/channels"
          component={B2cChannels}
        />
        <PrivateRoute path="/referrals/b2c/edit/test" component={B2cTest} />
        <PrivateRoute
          path="/referrals/b2c/edit/success"
          component={B2cSuccess}
        />
        <PrivateRoute
          path="/referrals/b2c/insights/:page"
          component={B2cInsights}
        />

        {/* Whatsapp Chat */}
        <PrivateRoute exact path="/chat/whatsapp" component={WhatsappChat} />

        {/* Membership */}
        <PrivateRoute exact path="/membership" component={MembershipIndex} />
        
        {/* Invite team members */}
        <PrivateRoute
          exact
          path="/invite-team-members"
          component={InviteTeamMembers}
        />
        <PrivateRoute
          component={CreateTeamMemberRole}
          path="/invite-team-members/:customUserId/role"
        />
        <PrivateRoute
          component={CreateTeamMemberAccount}
          path="/invite-team-members/:customUserId/account"
        />
        <PrivateRoute
          component={SelectTeamMemberStores}
          path="/invite-team-members/:customUserId/stores"
        />
        <PrivateRoute
          component={AddTeamMemberPermissions}
          path="/invite-team-members/:customUserId/permissions"
        />
        <PrivateRoute
          component={ReviewTeamMember}
          path="/invite-team-members/:customUserId/review"
        />
        <PrivateRoute
          component={SuccessTeamMember}
          path="/invite-team-members/:customUserId/success"
        />

        {/* mobile checklist */}
        <PrivateRoute exact path="/checklist/:page" component={Checklist} />
        <PrivateRoute path="/checklist/:page/:sub_page" component={Checklist} />

        {/* mobile menu */}
        <PrivateRoute exact path="/menu" component={NavigationMenu} />
        <PrivateRoute path="/menu/:sub_page" component={NavigationMenu} />

        <PrivateRoute
          path="/auto-refill-credits"
          component={AutoRefillCredits}
        />

        {/* cashier */}
        <CashierPublicRoute
          path="/cashier/:store_id"
          component={CashierLogin}
        />
        <CashierPublicRoute
          path="/cashier-store-id-not-found"
          component={CashierStoreIdNotFound}
        />
        <CashierPrivateRoute
          path="/cashier-home/:store_id"
          component={Cashier}
        />
        <CashierPrivateRoute
          path="/cashier-history/:store_id"
          component={CashierHistory}
        />
        <CashierPrivateRoute
          path="/redeem-reward/:store_id"
          component={RedeemReward}
        />
        <DefaultRoute path="" component={NotFound} />
      </Switch>
      <ReduxToastr
        timeOut={4000}
        newestOnTop={true}
        preventDuplicates
        position="top-right"
        transitionIn="fadeIn"
        transitionOut="fadeOut"
        getState={(state) => state.toastr}
        progressBar
        closeOnToastrClick
      />
    </BrowserRouter>
  );
};

export default Routes;
